import { quickSaleSchema } from '@alicerce/contracts';
import { Alert, Button, Card, CardBody, CardHeader, Field, formatBRL, Input, MoneyInput, PageHeader, Select, Spinner, Textarea, TextField } from '@alicerce/ui';
import { useQuery } from '@tanstack/react-query';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError, errorMessage } from '@/lib/api';
import { useDebounced } from '@/lib/hooks';
import { useApi, useCurrent } from '@/lib/session';
import type { Page } from '@/lib/types';
import type { Product } from '../catalog/types';

export const PAYMENT_METHOD_LABEL = {
  PIX: 'Pix',
  CREDIT_CARD: 'Cartão',
  BOLETO: 'Boleto',
  CASH: 'Dinheiro',
  OTHER: 'Outro',
} as const;
type Method = keyof typeof PAYMENT_METHOD_LABEL;

interface Line {
  variantId: string;
  label: string;
  quantity: number;
  unitPriceCents: number;
}

export interface CustomerHit {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
}

type SaleResult = { mode: 'order'; orderId: string; number: number; totalCents: number } | { mode: 'manual_sale'; saleId: string; totalCents: number };

export function variantLabel(p: Product, v: Product['variants'][number]) {
  return p.hasVariants ? `${p.name} · ${Object.values(v.options).join(' / ') || v.name}` : p.name;
}

/** Registro de uma venda feita fora da loja (balcão, WhatsApp, feira). */
export function QuickSalePage() {
  const api = useApi();
  const { isModuleActive } = useCurrent();
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim());
  const products = useQuery({
    queryKey: ['products', 'quick-sale', q],
    queryFn: () => api.get<Page<Product>>('/v1/core/products', { query: { q: q || undefined, status: 'ACTIVE', limit: 10 } }),
  });
  const [lines, setLines] = useState<Line[]>([]);
  const [customerMode, setCustomerMode] = useState<'none' | 'existing' | 'new'>('none');
  const [customer, setCustomer] = useState<CustomerHit | null>(null);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '' });
  const [method, setMethod] = useState<Method>('PIX');
  const [feeCents, setFeeCents] = useState<number | null>(0);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SaleResult | null>(null);
  // Uma chave por tentativa de venda: repetir o envio (rede caiu, clique duplo) não duplica.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  const total = lines.reduce((s, l) => s + l.quantity * l.unitPriceCents, 0);

  const add = (p: Product, v: Product['variants'][number]) =>
    setLines((ls) => {
      const found = ls.find((l) => l.variantId === v.id);
      if (found) return ls.map((l) => (l.variantId === v.id ? { ...l, quantity: l.quantity + 1 } : l));
      return [...ls, { variantId: v.id, label: variantLabel(p, v), quantity: 1, unitPriceCents: Number(v.priceCents) }];
    });
  const update = (variantId: string, patch: Partial<Line>) => setLines((ls) => ls.map((l) => (l.variantId === variantId ? { ...l, ...patch } : l)).filter((l) => l.quantity > 0));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = quickSaleSchema.safeParse({
      items: lines.map((l) => ({ variantId: l.variantId, quantity: l.quantity, unitPriceCents: l.unitPriceCents })),
      customerId: customerMode === 'existing' ? customer?.id : undefined,
      customer:
        customerMode === 'new'
          ? { name: newCustomer.name, phone: newCustomer.phone || undefined, email: newCustomer.email || undefined }
          : undefined,
      method,
      feeCents: feeCents ?? 0,
      notes: notes || undefined,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Confira os dados da venda.');
      return;
    }
    setSubmitting(true);
    try {
      setResult(await api.post<SaleResult>('/v1/core/quick-sale', parsed.data, { headers: { 'Idempotency-Key': idempotencyKey } }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setLines([]);
    setCustomerMode('none');
    setCustomer(null);
    setNewCustomer({ name: '', phone: '', email: '' });
    setFeeCents(0);
    setNotes('');
    setResult(null);
    setIdempotencyKey(crypto.randomUUID());
  };

  if (result) {
    return (
      <div className="mx-auto max-w-lg space-y-4">
        <PageHeader title="Venda registrada" />
        <Alert tone="success" title={`${formatBRL(result.totalCents)} em ${PAYMENT_METHOD_LABEL[method]}`}>
          {result.mode === 'order' ? (
            <>
              Virou o pedido nº {result.number}.{' '}
              <Link className="font-medium underline" to={`/pedidos/${result.orderId}`}>
                Ver pedido
              </Link>
            </>
          ) : (
            'A venda já entrou no resumo do seu negócio.'
          )}
        </Alert>
        <Button onClick={reset}>Registrar outra venda</Button>
      </div>
    );
  }

  return (
    <form className="space-y-6" onSubmit={submit} noValidate>
      <PageHeader
        title="Venda rápida"
        description={
          isModuleActive('checkout')
            ? 'Registre uma venda feita no balcão ou pelo WhatsApp. Ela vira um pedido pago e baixa o estoque.'
            : 'Registre uma venda feita no balcão ou pelo WhatsApp. Ela entra no resumo do seu negócio.'
        }
      />
      <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
        <Card>
          <CardHeader title="Produtos" />
          <CardBody className="space-y-3">
            <Input type="search" aria-label="Buscar produto" placeholder="Buscar por nome ou SKU" value={search} onChange={(e) => setSearch(e.target.value)} />
            {products.isPending ? (
              <Spinner />
            ) : products.isError ? (
              <Alert tone="danger">{errorMessage(products.error)}</Alert>
            ) : products.data.items.length === 0 ? (
              <p className="text-sm text-muted">
                Nenhum produto à venda encontrado. <Link to="/produtos/novo" className="text-brand-700 hover:underline">Cadastrar produto</Link>
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {products.data.items.flatMap((p) =>
                  p.variants.map((v) => (
                    <li key={v.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                      <span className="min-w-0 truncate">{variantLabel(p, v)}</span>
                      <span className="flex items-center gap-3">
                        <span className="tabular-nums text-muted">{formatBRL(Number(v.priceCents))}</span>
                        <Button size="sm" variant="secondary" aria-label={`Adicionar ${variantLabel(p, v)}`} onClick={() => add(p, v)}>
                          <Plus className="size-4" aria-hidden />
                        </Button>
                      </span>
                    </li>
                  )),
                )}
              </ul>
            )}
          </CardBody>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Venda" />
            <CardBody className="space-y-4">
              {lines.length === 0 ? (
                <p className="text-sm text-muted">Adicione os produtos vendidos.</p>
              ) : (
                <ul className="space-y-3" aria-label="Itens da venda">
                  {lines.map((l) => (
                    <li key={l.variantId} className="space-y-2 rounded-lg border border-line p-3 text-sm">
                      <div className="flex justify-between gap-2">
                        <span className="font-medium">{l.label}</span>
                        <button type="button" aria-label={`Remover ${l.label}`} className="text-muted hover:text-danger" onClick={() => update(l.variantId, { quantity: 0 })}>
                          <Trash2 className="size-4" aria-hidden />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="secondary" aria-label="Diminuir quantidade" onClick={() => update(l.variantId, { quantity: l.quantity - 1 })}>
                          <Minus className="size-4" aria-hidden />
                        </Button>
                        <span className="w-8 text-center tabular-nums" aria-label="Quantidade">
                          {l.quantity}
                        </span>
                        <Button size="sm" variant="secondary" aria-label="Aumentar quantidade" onClick={() => update(l.variantId, { quantity: l.quantity + 1 })}>
                          <Plus className="size-4" aria-hidden />
                        </Button>
                        <div className="ml-auto w-32">
                          <MoneyInput aria-label={`Preço de ${l.label}`} value={l.unitPriceCents} onChange={(c) => update(l.variantId, { unitPriceCents: c ?? 0 })} />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <p className="flex justify-between border-t border-line pt-3 font-semibold">
                <span>Total</span>
                <span className="tabular-nums" data-testid="quick-sale-total">
                  {formatBRL(total)}
                </span>
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="space-y-4">
              <Field label="Cliente">
                {(a11y) => (
                  <Select {...a11y} value={customerMode} onChange={(e) => setCustomerMode(e.target.value as typeof customerMode)}>
                    <option value="none">Sem cliente</option>
                    <option value="existing">Cliente cadastrado</option>
                    <option value="new">Novo cliente</option>
                  </Select>
                )}
              </Field>
              {customerMode === 'existing' && <CustomerSearch value={customer} onChange={setCustomer} />}
              {customerMode === 'new' && (
                <div className="space-y-3">
                  <TextField label="Nome do cliente" value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} />
                  <TextField label="Telefone" type="tel" value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} />
                  <TextField label="E-mail" type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} />
                </div>
              )}
              <Field label="Forma de pagamento">
                {(a11y) => (
                  <Select {...a11y} value={method} onChange={(e) => setMethod(e.target.value as Method)}>
                    {Object.entries(PAYMENT_METHOD_LABEL).map(([k, label]) => (
                      <option key={k} value={k}>
                        {label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Taxa da maquininha" hint="O que a maquininha ou o banco cobrou. Entra como custo no financeiro.">
                {(a11y) => <MoneyInput {...a11y} value={feeCents} onChange={setFeeCents} />}
              </Field>
              <Field label="Observações">{(a11y) => <Textarea {...a11y} value={notes} onChange={(e) => setNotes(e.target.value)} />}</Field>
              {error && <Alert tone="danger">{error}</Alert>}
              <Button type="submit" className="w-full" loading={submitting} disabled={lines.length === 0}>
                Registrar venda de {formatBRL(total)}
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>
    </form>
  );
}

export function CustomerSearch({ value, onChange }: { value: CustomerHit | null; onChange: (c: CustomerHit | null) => void }) {
  const api = useApi();
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());
  const hits = useQuery({
    queryKey: ['customers', 'search', q],
    queryFn: () => api.get<Page<CustomerHit>>('/v1/core/customers', { query: { q, limit: 5 } }),
    enabled: q.length >= 2,
  });
  if (value) {
    return (
      <p className="flex items-center justify-between rounded-lg bg-canvas px-3 py-2 text-sm">
        <span>
          {value.name} <span className="text-muted">{value.phone ?? value.email}</span>
        </span>
        <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
          Trocar
        </Button>
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <Input type="search" aria-label="Buscar cliente" placeholder="Nome, telefone ou e-mail" value={text} onChange={(e) => setText(e.target.value)} />
      {hits.data && (
        <ul className="divide-y divide-line rounded-lg border border-line text-sm">
          {hits.data.items.length === 0 && <li className="px-3 py-2 text-muted">Nenhum cliente encontrado.</li>}
          {hits.data.items.map((c) => (
            <li key={c.id}>
              <button type="button" className="w-full px-3 py-2 text-left hover:bg-canvas" onClick={() => onChange(c)}>
                {c.name} <span className="text-muted">{c.phone ?? c.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
